import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  IdTokenResult,
} from 'firebase/auth';
import { getFirebaseAuth } from '../config/firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  idToken: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [idToken, setIdToken] = useState<string | null>(null);

  const checkAdminStatus = async (user: User): Promise<boolean> => {
    try {
      const tokenResult: IdTokenResult = await user.getIdTokenResult();
      return tokenResult.claims['admin'] === true;
    } catch (error) {
      console.error('Failed to check admin status:', error);
      return false;
    }
  };

  const getAndStoreToken = async (user: User): Promise<void> => {
    try {
      const token = await user.getIdToken();
      setIdToken(token);
    } catch (error) {
      console.error('Failed to get ID token:', error);
      setIdToken(null);
    }
  };

  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        const admin = await checkAdminStatus(firebaseUser);
        setIsAdmin(admin);
        await getAndStoreToken(firebaseUser);

        if (!admin) {
          console.warn('User is not an admin');
        }
      } else {
        setIsAdmin(false);
        setIdToken(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      const auth = getFirebaseAuth();
      const result = await signInWithEmailAndPassword(auth, email, password);

      const admin = await checkAdminStatus(result.user);

      if (!admin) {
        await firebaseSignOut(auth);
        throw new Error('You do not have admin privileges');
      }

      await getAndStoreToken(result.user);
    } catch (error) {
      console.error('Sign in failed:', error);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      const auth = getFirebaseAuth();
      await firebaseSignOut(auth);
      setUser(null);
      setIsAdmin(false);
      setIdToken(null);
    } catch (error) {
      console.error('Sign out failed:', error);
      throw error;
    }
  };

  const refreshToken = async (): Promise<void> => {
    if (user) {
      await getAndStoreToken(user);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    isAdmin,
    idToken,
    signIn,
    signOut,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
